import type { CanvasChatMessage } from '@/pages/canvas/canvas-types';
import { normalizeChatLinks } from '../../pages/canvas/chat-links';

/** Keep saved rosters for follow-up context, but show the result beside its artifact. */
export function taskResultContent(message: CanvasChatMessage): string {
  let content = message.content || '';
  if (message.role !== 'assistant') return content;
  // Only the displayed gallery's own links and recognizable generation rosters
  // are redundant. Keep persisted text, conversational answers and warnings.
  const galleryUrls = new Set(message.imageSlots?.length
    ? message.imageSlots.flatMap(slot => !slot.failed && slot.resolved ? [slot.resolved] : [])
    : message.thumbnails ?? []);
  if (galleryUrls.size) {
    const delivery = /^\s*(?:#+\s*)?(?:\*\*)?(\d+)\s+of\s+(\d+)\s+(?:(?:selected\s+)?(?:ads?|images?)\s+)?delivered\b/i;
    const adHeading = /^\s*(?:#+\s*)?(?:[-*]\s*)?(?:\*\*)?Ad\s+\d+\b(?:\*\*)?\s*(?:[:—-].*)?$/i;
    const hasWarning = (text: string) => /\b(?:fail(?:ed|ure|ures)?|incomplete|missing|errors?|warnings?|rejected|cancelled|canceled|timed out|needs? review|quality review|not quality.verified|unverified|could(?:n['’]t| not)|unavailable)\b/i.test(text.replace(/\b(?:no|0) failures?[.!]?/gi, ''));
    // Old completion narration remains saved for context; the gallery is the roster.
    const variationIntro = /^Here are your \d+ variations?\b/i;
    const variationLines = content.split('\n');
    if (variationLines.some(line => variationIntro.test(line.replace(/\*\*/g, '').trim()))) {
      content = variationLines.filter(line => {
        const plain = line.replace(/\*\*/g, '').trim();
        if (hasWarning(plain)) return true;
        return !variationIntro.test(plain)
          && !/^(?:\d+[.)]\s*)?Remix\s+\d+\s*[.!]?$/i.test(plain)
          && !/^Variation\s+\d+\s*[–—-]\s*Remix\s+\d+$/i.test(plain)
          && !/^\d+[.)]\s*$/.test(plain)
          && !/^Let me know if you(?:'d| would)? like\b/i.test(plain);
      }).join('\n').trim();
      content = `${galleryUrls.size} image${galleryUrls.size === 1 ? '' : 's'} ready.${content ? `\n\n${content}` : ''}`;
    }
    const legacyResult = content.split('\n').some(line => delivery.test(line) || adHeading.test(line) || /^\s*Made \d+ ads?:/i.test(line));
    if (legacyResult) {
      content = content.split('\n').filter(line => {
        const plain = line.replace(/\*\*/g, '').replace(/^\s*[-*]\s+/, '').trim();
        const outcome = plain.match(delivery);
        if (outcome) {
          return Number(outcome[1]) < Number(outcome[2]) || hasWarning(plain);
        }
        if (hasWarning(plain)) return true;
        if (adHeading.test(line)) return false;
        if (!plain) return true;
        if (/^(?:-{3,}|Here are your \d+ completed ads? for .+:)$/i.test(plain)) return false;
        if (hasWarning(plain)) return true;
        if (/^(?:Made \d+ ads?:|No failures[.!]?(?:\s.*)?|Let me know if you (?:want|need) .*)$/i.test(plain)) return false;
        if (/^(?:Headline|Concept|Body(?: copy)?|CTA|Aspect ratio)\s*:/i.test(plain)) return false;
        return true;
      }).join('\n');
    }
    const link = /!?\[([^\]]*)\]\((?:<([^>\n]+)>|([^\s)\n]+))(?:\s+"[^"\n]*")?\)/g;
    content = normalizeChatLinks(content).split('\n').map(line => {
      if (galleryUrls.has(line.trim().replace(/^<|>$/g, ''))) return '';
      const withoutLinks = line.replace(link, (all, _label, angledUrl, url) => galleryUrls.has(angledUrl ?? url) ? '' : all);
      if (withoutLinks === line) return line;
      if (/^\s*(?:[-*•]|\d+[.)])?\s*$/.test(withoutLinks)) return '';
      return line.replace(link, (all, label, angledUrl, url) => galleryUrls.has(angledUrl ?? url) ? label : all);
    }).join('\n').replace(/\n{3,}/g, '\n\n').trim();
  }
  const choices = message.suggestionPills;
  if (choices && choices.pills.length > 4 && (/\bproduct\b/i.test(choices.question) || choices.pills.some(pill => pill.forcedTool?.arguments.productId != null))) {
    const normalize = (value: string) => value.replace(/\*\*/g, '').replace(/[’‘]/g, "'").trim().toLowerCase();
    const labels = new Set(choices.pills.map(pill => normalize(pill.label)));
    return content.split('\n').filter(line => {
      const item = line.match(/^\s*(?:[-*•]|\d+[.)])\s+(.+)$/);
      return !item || !labels.has(normalize(item[1]));
    }).join('\n').replace(/\n{3,}/g, '\n\n').trim();
  }
  const paragraph = content.split('\n\n')[0];
  if (message.angleSetCard && message.conceptsRun?.status === 'ready') {
    const legacy = /^\d+ angle concepts? planned for .+ — saved as ".+" \(it's on the brand's Ad Angles tab too\)\. Review the full sheet in the doc, then hit "Make these ads" to generate one ad per angle\.$/;
    if (legacy.test(paragraph) && message.conceptsRun.productName) {
      const count = message.angleSetCard.angleCount;
      if (typeof count === 'number') return `${count} concept${count === 1 ? '' : 's'} ready for ${message.conceptsRun.productName}.`;
    }
    if (/^\d+ concepts? ready for .+\.$/.test(paragraph) && content.startsWith(`${paragraph}\n\nSaved concepts:\n`)) return paragraph;
  }
  if (message.agentDocument && /^\d+ ad ideas? ready for .+\.$/.test(paragraph) && content.startsWith(`${paragraph}\n\nSaved ideas:\n`)) return paragraph;
  return content;
}
